import React, {useState} from "react";
import { Font } from "@react-pdf/renderer";
import axios from 'axios';
import ReportForm from 'components/Report/Form/Form';
import Preview from 'components/Report/Preview';
import LoadingOverlay from 'components/Common/Loading/LoadingOverlay';

import * as fontPoppinsRegular from 'assets/fonts/Poppins/Poppins-Regular.ttf';
import * as fontPoppinsLight from 'assets/fonts/Poppins/Poppins-Light.ttf';
import * as fontPoppinsMedium from 'assets/fonts/Poppins/Poppins-Medium.ttf';
import * as fontPoppinsBold from 'assets/fonts/Poppins/Poppins-Bold.ttf';
import * as fontPoppinsSemiBold from 'assets/fonts/Poppins/Poppins-SemiBold.ttf';

import * as fontRobotoMonoMedium from 'assets/fonts/Roboto_Mono/static/RobotoMono-Medium.ttf';
import * as fontRobotoMonoRegular from 'assets/fonts/Roboto_Mono/static/RobotoMono-Regular.ttf';
import {CONDITIONS, CONDITIONS_BY_PANEL} from "components/Report/index";
import camelCase from "components/Report/services/getStrToCamelCase";
import {gql, useMutation} from "@apollo/client";
export const registerFont = () => {
  Font.register({
    family: 'Poppins', fonts: [
      {src: fontPoppinsLight, fontStyle: 'normal', fontWeight: 'light'},
      {src: fontPoppinsRegular, fontStyle: 'normal', fontWeight: 'normal'},
      {src: fontPoppinsMedium, fontStyle: 'normal', fontWeight: 'medium'},
      {src: fontPoppinsSemiBold, fontStyle: 'normal', fontWeight: 'semibold'},
      {src: fontPoppinsBold, fontStyle: 'normal', fontWeight: 'bold'}
    ]
  });

  Font.register({
    family: 'RobotoMono', fonts: [
      {src: fontRobotoMonoRegular, fontStyle: 'normal', fontWeight: 'normal'},
      {src: fontRobotoMonoMedium, fontStyle: 'normal', fontWeight: 'medium'},
    ]
  });

  Font.registerHyphenationCallback(word => [word]);
}

registerFont();

const GQL_UPDATE_REPORT_VALUES = gql`
  mutation updateConfigReportValues($input: ConfigReportValuesInput!) {
    updateConfigReportValues(input: $input) {
        config {
          person {
            id
          },
          disclaimerLanguage,
          reportInfo,
          pgtpPanelEthnicity
        }
    }
  }
`;

const GQL_UPDATE_REPORT_CHECKS = gql`
  mutation updateReportChecks($input: ReportChecksInput!) {
    updateReportChecks(input: $input) {
        config {
          patient {
            id
          },
          patientPartnerNameDob,
          embryoNumbers,
          embryoGradeBiopsyDate,
          samplesReceivedDate,
          referringClinician,
          karyotypes,
          formatting,
          isAmended,
          isRevised,
          isPreliminary,
          isFinal
        }
    }
  }
`;

const GQL_UPDATE_M2_REPORT_CHECKS = gql`
  mutation updateM2ReportChecks($input: M2ReportChecksInput!) {
    updateM2ReportChecks(input: $input) {
        config {
          patient {
            id
          }
          patientDemographics
          clinicReferringPhysician
          sampleInformation
          finalSampleResult
        }
    }
  }
`;

const GQL_UPDATE_REPORT_CONDITIONS = gql`
    mutation updateReportConditions($input: ReportConditionsInput!) {
        updateReportConditions(input: $input) {
            case {
                id
                reportConditions
            }
        }
    }
`;

/**
 * @returns {string} - a blank panel for a sample category.
 */
const generateBlankPanel = () => {
  const conditionsJson = Object.keys(CONDITIONS).map(disease =>
    `{"name":"${disease}","risk_percentile":0,"risk_value":0,"risk_avg":0,"risk_ratio":0,"risk":0,"index":0,"qaly_diff":0,"title":"${CONDITIONS[disease]}"}`
  );
  return '[' + conditionsJson.join(',') + ']';
};


// karyotype signifies segmental aneuploidy if it contains 'del' or 'dup', but not + or -
// const segmentalAneuploidyRegex = /^(?=.*\b(del|dup))(?!.*[+-]).*$/;

/**
 * Round risk percentile to a whole value (i.e. no decimal places)
 * @param riskPercentile
 * @returns {null|string}
 */
const parseRiskPercentile = (riskPercentile) => {
  if (riskPercentile || riskPercentile === 0) {
    if (riskPercentile < 9.5) return parseFloat(riskPercentile).toPrecision(1);
    if (riskPercentile < 99.5) return parseFloat(riskPercentile).toPrecision(2);
    return parseFloat(riskPercentile).toPrecision(3);
  }
  return null;
};


const initialReportChecks = {
  patientPartnerNameDob: false,
  embryoNumbers: false,
  embryoGradeBiopsyDate: false,
  samplesReceivedDate: false,
  referringClinician: false,
  karyotypes: false,
  formatting: false,
  isAmended: false,
  isRevised: false,
}

const initialM2ReportChecks = {
  patientDemographics: false,
  clinicReferringPhysician: false,
  sampleInformation: false,
  finalSampleResult: false
}

/**
 * <ReportDocument> component.
 * Display ReportForm on left, and PDF preview on right.
 * @param {object} props - {data} - {patient/case data, selectPanel, diseases, language}
 *                      see CASES in components/Report/index.js for patient/case fields
 */
export default (props) => {
  const [updateReportValues] = useMutation(GQL_UPDATE_REPORT_VALUES);
  const [updateReportChecks] = useMutation(GQL_UPDATE_REPORT_CHECKS);
  const [updateM2ReportChecks] = useMutation(GQL_UPDATE_M2_REPORT_CHECKS);
  const [updateReportConditions] = useMutation(GQL_UPDATE_REPORT_CONDITIONS);

  /**
   * Format sample data for output, then sort into arrays: all, failed, aneuploid, and euploid
   *
   * @param {object} samples - array of samples from case data
   *                           see CASES in components/Report/index.js for sample fields
   * @param {boolean} hideSexInKaryotype
   * @param selectedPanel
   * @param diseases
   * @returns {{all: *[], euploid: *[], aneuploid: *[], failed: *[]}}
   */
  const transformSampleData = (samples, hideSexInKaryotype = false, selectedPanel = null, diseases = []) => {
    const euploid = [];
    const aneuploid = [];
    const failed = [];
    const all = [];
  
    if (samples) {
      samples
        .forEach((sample) => {
          const parsedPanel = JSON.parse(sample.panel);
          let roundedPanelVals = Object.assign(parsedPanel, {});

          for (let disease in Object.keys(parsedPanel)) {
            if (Object.keys(parsedPanel[disease]).length){     
              roundedPanelVals[disease].risk_percentile = parseRiskPercentile(parsedPanel[disease].risk_percentile);
              roundedPanelVals[disease].risk_ratio = ((parsedPanel[disease].risk_ratio) || (parsedPanel[disease].risk_ratio === 0)) ? parseFloat(parsedPanel[disease].risk_ratio).toPrecision(2) : null;
              roundedPanelVals[disease].risk_value = ((parsedPanel[disease].risk_value) || (parsedPanel[disease].risk_value === 0)) ? (parseFloat(parsedPanel[disease].risk_value) * 100).toPrecision(2) : null;
              roundedPanelVals[disease].risk_avg = ((parsedPanel[disease].risk_avg) || (parsedPanel[disease].risk_avg === 0)) ? (parseFloat(parsedPanel[disease].risk_avg) * 100).toPrecision(2) : null;
            }
          }
          sample = {...sample, panel: JSON.stringify(roundedPanelVals)}

          if (hideSexInKaryotype) {
            sample = {...sample, karyotype: sample.karyotype.replace(/XX/g, '--').replace(/XY/g, '--').replace(/X/g, '--').replace(/Y/g, '--')}
          }

          if (sample.complexAneuploid) {
            sample = {...sample, sex: '-'}
          }

          if (selectedPanel && sample.panel) {
            const allowedConditions = CONDITIONS_BY_PANEL[selectedPanel]
            sample = {...sample, allowedConditions: allowedConditions, diseases: data.diseases}
            //sample = {...sample, panel: sample.panel.filter((item) => allowedConditions.includes(item.name))}
          }

          if (sample.noAmp || sample.inconclusive || sample.noResult) {
            let aUpdated = sample.karyotype
            if (sample.noAmp) {
              aUpdated = 'No Amp'
            }

            if (sample.inconclusive) {
              aUpdated = 'Inconclusive'
            }
            sample = {...sample, sex: '-'}

            failed.push({...sample, pgt_a: '', sex: '', karyotype: aUpdated});
          } else if (sample.aneuploid || sample.complexAneuploid) {
            aneuploid.push({...sample, panel: generateBlankPanel(), pgt_a: 'aneuploid'});
          } else {
            euploid.push({...sample, pgt_a: 'euploid'})
          }

          all.push(sample)
        });
    }

    return {euploid, aneuploid, failed, all}
  }

  /** case data + report settings.
   * NOTE: This is gql version of case (patient), so all fields are camelCased.  E.g. caseId instead of case_id.
   * */
  const [data, setData] = useState({
    ...props.data,  // gql data read from case (patient) + other
    analysisVersion: props.data.analysisVersion, //'v22.6 06/05/2021',
    reportVersion: props.data.reportVersion, //'v2.2.0 05/03/2021',
    showPTableForAneuploid: true,
    isAmended: props.data.reportInfo.includes('amended'),
    layout: 1,
    reportChecks: (props.data.reportChecks || {...initialReportChecks}),
    m2ReportChecks: (props.data.m2ReportChecks || {...initialM2ReportChecks}),
  });

  const [documentLoading, setDocumentLoading] = useState(false)
  const [generating, setGenerating] = useState(false);

  /** Preserve report settings so that they can be used as default
   *  for subsequent reports for same patient
   */
  const saveSettings = async (values) => {
    console.log('SKR Document.js: saveSettings', values);
    if (values) {
      await updateReportValues({
        variables: {
          input: {
            "person": {
              "id": values.patient.id
            },
            "disclaimerLanguage": data.testTypes.slice().sort().join(',') + '||||' + values.language,
            "reportInfo": values.reportInfo,
            "pgtpPanelEthnicity": values.selectedPanel
          }
        }
      });
      if (values.diseases && values.diseases.length > 0) {
        await updateReportConditions({
          variables: {
            input: {
              "id": values.id,
              "reportConditions": JSON.stringify(values.diseases)
            }
          }
        });
      }

      if (data.testTypes.includes("A") || data.testTypes.includes('A+')) {    // If PGT report ...
        await updateReportChecks({
          variables: {
            input: {
              "patient": {
                "id": values.id
              },
              "patientPartnerNameDob": values.reportChecks.patientPartnerNameDob,
              "embryoNumbers": values.reportChecks.embryoNumbers,
              "embryoGradeBiopsyDate": values.reportChecks.embryoGradeBiopsyDate,
              "samplesReceivedDate": values.reportChecks.samplesReceivedDate,
              "referringClinician": values.reportChecks.referringClinician,
              "karyotypes": values.reportChecks.karyotypes,
              "formatting": values.reportChecks.formatting,
              "isAmended": values.reportChecks.isAmended,
              "isRevised": values.reportChecks.isRevised,
              "isPreliminary": values.reportChecks.isPreliminary,
              "isFinal": values.reportChecks.isFinal
            }
          }
        });
      } else {  // ... is M2 report
        await updateM2ReportChecks({
          variables: {
            input: {
              "patient": {
                "id": values.id
              },
              "patientDemographics": values.m2ReportChecks.patientDemographics,
              "clinicReferringPhysician": values.m2ReportChecks.clinicReferringPhysician,
              "sampleInformation": values.m2ReportChecks.sampleInformation,
              "finalSampleResult": values.m2ReportChecks.finalSampleResult
            }
          }
        });
      }
    }
  }

  /**
   * Handle changes to fields on report form.  Update db, reload document and if suffixes are present, issue report.
   * @param newData - new values for report form fields
   * @param reportSuffixes - concatenated suffixes to report name, e.g. _preliminary, _amended_final, "none" if none
   *                         When omitted or null, does not issue report.
   */
  const onChange = (newData, reportSuffixes=null) => {
    setDocumentLoading(true);
    const newData2 = { ...data, ...newData };
    console.log('SKR Document.js: newData2', newData2);
    setData(newData2);
    saveSettings({...newData2}).then(r => {
      if (reportSuffixes !== null) issueReport(newData2, reportSuffixes).then(r => r);
    });
    setTimeout(() => {
      setDocumentLoading(false);
    }, 500);
  };

  const sampleData = transformSampleData(data.samples, data.hideSexInKaryotype, data.selectedPanel, data.diseases ? data.diseases : [])

  /** Divide report language into sections
   * @param language - string of HTML from report form
   * @param testType - "PGT" or "M2"
   * @returns {Object} - object containing report's language formatted as {sectionName: sectionLanguage}
   * */
  const splitLanguageForValidity = (language, testType) => {
    if (testType !== "M2") {
      const sectionArr = language.split("<h3>");
      let formatted = {};
      sectionArr.forEach(section => {
        let value = "<h3>"
        let key = ""
        const regexp = /<h3>(.*?)<\/h3>/g

        if (section.length) {
          value += section;
          key += value.match(regexp)
          const header = key.replace(/<\/?h3>/g,'');
          if (header !== "Genetic counseling is recommended."){
            const camelcased = camelCase(header.slice(0, -1))
            formatted[camelcased] = value
          }
          else {
            formatted["pgtMDisclaimer"] = value
          }
        }
      })
      return formatted
    }
    else { // testType is M2
      return {
        resultInterpretation: '<p><strong>Result Interpretation:</strong></p><p>Genotyping for the Annexin A5 (ANXA5) M2 haplotype was performed. "Negative" indicates that the M2 haplotype was not detected. "Heterozygous Positive" indicates that one copy of the M2 haplotype was detected. "Homozygous Positive" indicates that two copies of the M2 haplotype were detected. Previous studies suggest that individuals who are positive for at least one copy of the M2 haplotype may benefit from antithrombotic intervention with low molecular weight heparin in order to achieve similar success rates as non-. carrier individuals (Fishel et al. Precision Medicine in Assisted Conception: A Multicenter Observational Treatment Cohort Study of the Annexin AS M2 Haplotype as a Biomarker for Antithrombotic Treatment to Improve Pregnancy Outcome. EBioMedicine, 2016).</p><p><br></p>',
        methodsAndLimitations: '<p><strong>Methods and Limitations:</strong></p><p>This test determines the status of carrying the ANXA5 M2 haplotype in an individual using quantitative real-time PCR. When used on positive controls the test performs with &gt;99% accuracy. Results are in reference to the normal human genome (hg19; NG 032042.1:g.[4937G;4956A;4982T;5031G]). Positive and negative controls performed as expected. This service is an adjunct to the evaluation of the referring clinician and is designed to contribute to his or her overall assessment. Results must be interpreted in the context of clinical findings. This test was developed. and its performance characteristics determined by Genomic Prediction Clinical Laboratory. It has not been cleared or approved by the U.S. Food and Drug Administration (FDA).</p>'
      }
    }
  }

  /** Get the CPV1 backend auth token passed to us by cpv1 */
  const getToken = () => {
    try {
        const obj = JSON.parse(window.localStorage.getItem('persist:clinic-portal'))
        const { auth } = obj
        const t = JSON.parse(auth)
        return t.token
    }
    catch(e){
      console.error('error obtaining token:', e)
      return false
    }
  }

  /** Remove optional fields from validate object when they are empty */
  const handleOptionalFields = (optionalFields, validate) => {
    optionalFields.forEach(field => {
      if (typeof validate[field] === 'string' && !validate[field].length) {
        delete validate[field]
      }
      if (typeof validate[field] === 'object' && !Object.values(validate[field]).length) {
        delete validate[field]
      }
    })
  }

  /**
   * Issue a report for this case (when user clicks "Issue Report" button)
   * This is a little front-end to mapDataToSchema that first determines the name of report file to generate.
   * @param data - copy of case data + report settings
   * @param reportSuffixes - concatenated suffixes to report name, e.g. _preliminary, _amended_final, "none" if none
   */
  const issueReport = async (data, reportSuffixes) => {
    const token = getToken() ? getToken() : window.localStorage.getItem('persist:clinic-portal');
    if (!token) alert('Session lost. Please log in again.');
    setGenerating(true);

    try {
      // This adds sequence number to report name, when necessary, to avoid overwriting existing reports (and to add .pdf)
      const resp = await axios({
        method: 'GET',
        baseURL: process.env.REACT_APP_API_HOST,
        url: `patient-reports-next-filename/${data.caseId}/${reportSuffixes}/`,
        headers: {
          "Authorization": `Token ${token}`
        }
      });

      const r = resp.data;
      if (r.status === 'success')
        return mapDataToSchema(token, {...data}, r.file_name, r.destination_patient_id);
      else
        console.error(r.detail);
    }
    catch(error) {
      throw new Error(error)
    }
  }

  /**
   * Call CPv1 backend to validate case data, then generate a PDF using Server-Side Report Generation
   * @param {string} token - API access token
   * @param {object} data - copy of case data + report settings
   * @param {string} reportName - report file name, e.g. caseId.pdf or caseId_preliminary_2.pdf
   * @param {int} destination_patient_id - id of case (patient) to which report is being issued
   *            (only set when rebiopsy.  Otherwise is None and data's id is used)
   */
  const mapDataToSchema = async (token, data, reportName, destination_patient_id) => {
    setGenerating(true);
    let testTypeParam = '';
    let validate;
    if (data.testTypes.includes("A") || data.testTypes.includes('A+')) {    // If PGT report ...
      validate = {
        testTypes: [...data.testTypes],
        caseId: data.caseId,
        analysisVersion: data.analysisVersion,
        reportVersion: data.reportVersion,
        reportSigned: data.reportSigned,
        sampleReceived: data.samplesReceivedAt,
        sampleType: data.sampleType,
        patientName: data.patientName,
        partnerName: data.partnerName || "",
        patientDOB: data.patient.dob,
        partnerDOB: (data.partner && data.partner.dob) || "",
        patientEmail: data.patient.email || "",
        partnerEmail: (data.partner && data.partner.email) || "",
        patientTelephone: data.patient.phone || "",
        partnerTelephone: (data.partner && data.partner.phone) || "",
        patientAddress: data.patient.address ? JSON.parse(data.patient.addressForSsr) : {},
        partnerAddress: (data.partner && data.partner.address) ? JSON.parse(data.partner.addressForSsr) : {},
        diseaseList: data.diseases,
        patientEthnicity: data.patient.ethnicity,
        partnerEthnicity: (data.partner && data.partner.ethnicity) || "",
        referringClinician: data.clinicianName || "",
        referringClinicianTelephone: data.clinicianPhone || "",
        referringClinicianAddress: JSON.parse(data.clinicAddress) || {},
        referringClinic: data.clinicName,
        reportCycleNumber: data.reportCycleNumber,
        cycleNumber: data.cycleNumber,
        reportEmbryoGrade: data.reportEmbryoGrade,
        reportEmbryoId: data.reportEmbryoId,
        reportBiopsyDay: data.reportBiopsyDay,
        info: data.reportInfo,
        pgtpPanel: data.selectedPanel,
        results: [...data.samples],
        layout: data.layout,
        isAmended: data.isAmended,
        language: splitLanguageForValidity(data.language, "PGT"),
        selectedListOfEmbryos: ["1", "2", "3"],
        //maybe handle in FE? not clear
        logic: {
          hideSexInKaryotype: data.hideSexInKaryotype || false,
          hidePartner: data.hidePartner || false,
          showPTableForAneuploid: data.showPTableForAneuploid || false
        }
      }

      testTypeParam += 'pgt'
      const optionalFields = ['patientEmail', 'partnerEmail', 'patientTelephone', 'partnerTelephone', 'patientAddress', 'partnerAddress', 'partnerDOB', 'clinicLogo', 'referringClinicianTelephone', 'referringClinicianAddress', 'info'];
      handleOptionalFields(optionalFields, validate);
    } else {  // ... is M2 report
      validate = {
        testTypes: [...data.testTypes],
        caseId: data.caseId,
        analysisVersion: data.analysisVersion,
        reportVersion: data.reportVersion,
        reportSigned: data.reportSigned,
        sampleReceived: data.samplesReceivedAt || '01/01/1999',
        sampleType: data.sampleType,
        patientName: data.patientName,
        patientDOB: data.patient.dob,
        patientEmail: data.patient.email || "",
        clientReference: data.clientReference,
        patientTelephone: data.patient.phone || "",
        patientAddress: data.patient.address ? JSON.parse(data.patient.addressForSsr) : {},
        referringClinician: data.clinicianName || "",
        referringClinicianTelephone: data.clinicianPhone || "",
        referringClinicianAddress: JSON.parse(data.clinicAddress) || {},
        referringClinic: data.clinicName,
        info: data.reportInfo,
        results: [...data.samples],
        salivaSamples: [...data.salivaSamples],
        layout: data.layout,
        isAmended: data.isAmended,
        test_performed_by: data.testPerformedBy || "",
        language: splitLanguageForValidity(data.language, "M2"),
        // results: data.testTypes.includes("M2") ? getResults(data.salivaSamples) : getResults(data.samples),
        //maybe handle in FE? not clear
        logic: {
          blinded: data.blinded,
          showDOBIfBlinded: data.show_db || false,
          hidePartner: data.hidePartner || false
        }
      }
      testTypeParam += 'm2';
      const optionalFields = ['patientEmail', 'patientTelephone', 'patientAddress', 'clinicLogo', 'referringClinicianTelephone', 'referringClinicianAddress', 'info']
      handleOptionalFields(optionalFields, validate);
    }

    const validate_result = await axios({
      method: 'POST',
      contentType: 'application/json',
      baseURL: process.env.REACT_APP_API_HOST,
      data: {"schema_v": testTypeParam, case: validate, "report_name": reportName},
      url: `validate-generate-pdf/`,
      headers: {
        "Authorization": `Token ${token}`
      }
    })

    if (validate_result?.data?.status === 'error validating schema') {
      validate_result.data.errors.forEach(e => alert(
        `Error validating report - please send error to IT\n${e.path}\n${e.message}`
      ));
      setGenerating(false);
      return;
    }

    if (validate_result.data.body === undefined || validate_result.data.body.StatusCode !== 200
      || !validate_result.data.body.Payload || validate_result.data.body.Payload.length === 0 || validate_result.data.body.Payload[0] === "{}") {
      alert(`Error creating report - please alert IT`);
      setGenerating(false);
      return;
    }

    // Report created successfully.  Add it to case.  Auto-deliver if conditions met.

    const reportAddedMsg = (msg) => {
      alert(`Report added to Pending Reports.\n${msg}\n\n(refresh case page if report does not appear)`)
    };

    const s3Key = JSON.parse(validate_result.data.body.Payload[0]).key;  // key = uuid + '.pdf'
    const resp = await axios({
      method: 'PUT',
      contentType: 'application/json',
      baseURL: process.env.REACT_APP_API_HOST,
      data: {
        "file_name": reportName,
        file_type: 'pending_reports',
        url: `https://d2cgp14cvu7uqn.cloudfront.net/${s3Key}`
      },
      url: `file/${destination_patient_id ? destination_patient_id : data.id}/`,
      headers: {
        "Authorization": `Token ${token}`
      }
    });
    if (resp.status !== 200)
      alert(`Error adding file to pending reports - please alert IT - ${resp.data.detail}`);
    else {
      if (!resp.data.autodelivery || resp.data.autodelivery === 'n/a') {
        if (resp.data.reasons_not_qualified && resp.data.reasons_not_qualified !== 'none')
          reportAddedMsg(`(not delivered - ${resp.data.detail})\n(not qualified - ${resp.data.reasons_not_qualified})`)
        else
          reportAddedMsg(`(not delivered - ${resp.data.detail})`);  // autodelivery not applicable
      } else if (resp.data.autodelivery === 'not_qualified')
        reportAddedMsg(`(not delivered - ${resp.data.detail})`);
      else if (resp.data.autodelivery === 'error')
        reportAddedMsg(`delivery error: ${resp.data.detail}`);  // autodelivery failed
      else if (resp.data.autodelivery === 'success')
        reportAddedMsg(resp.data.detail);  // lists actions
      else
        reportAddedMsg(`unknown error in delivery\n${resp.data.autodelivery} - ${resp.data.detail}`);
    }
    setGenerating(false);
  }

  return (
    <div style={{paddingRight: 800}}>
      <ReportForm
        documentLoading={documentLoading}
        initialData={{...data}}
        onChange={onChange}
      />
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 50,
          bottom: 0,
          border: 'none',
          width: 800
        }}
      >
        <LoadingOverlay show={generating} msg="Generating Report" />
        { data &&
          <Preview data={data} sampleData={sampleData} title="Report" />
        }
      </div>
    </div>
  )
}
